import React, { useState, useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { CircularProgress, TextField, InputAdornment } from "@mui/material";
import WarningIcon from '@mui/icons-material/Warning';
import { red } from '@mui/material/colors';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import MDButton from "components/MDButton";
import IconButton from "@mui/material/IconButton";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Swal from "sweetalert2";
import api from "core/interceptors/api";
import { disableBackNavigation } from "core/utils/storage";
import { useNavigate } from "react-router-dom";
import LogoutButton from "layouts/logout";
import CouponAddModel from "./coupon_add_modal";
import CouponEditModal from "./coupon_edit_modal";
import SearchIcon from '@mui/icons-material/Search';

export default function CouponManagement() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredCoupons, setFilteredCoupons] = useState([]);

  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [editInput, setEditInput] = useState({});
  const [loading, setLoading] = useState(true);
  const [coupons, setCoupons] = useState([]);
  const [deleteCouponId, setDeleteCouponId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    disableBackNavigation();
    fetchCoupons();
  }, []);

  useEffect(() => {
    const lowerCaseQuery = searchQuery.toLowerCase();
    const newFilteredCoupons = coupons.filter(coupon => {
      const couponIdMatch = coupon.coupon_id?.toLowerCase().includes(lowerCaseQuery);
      const percentageMatch = String(coupon.percentage)?.toLowerCase().includes(lowerCaseQuery);
      const startDateMatch = coupon.start_date?.substring(0, 10).toLowerCase().includes(lowerCaseQuery);
      const endDateMatch = coupon.end_date?.substring(0, 10).toLowerCase().includes(lowerCaseQuery);
      return couponIdMatch || percentageMatch || startDateMatch || endDateMatch;
    });
    setFilteredCoupons(newFilteredCoupons);
    setPage(0);
  }, [searchQuery, coupons]);

  const handleClose = () => {
    setEditInput({});
    setOpenAddModal(false);
    setOpenEditModal(false);
    setOpenDeleteModal(false);
    fetchCoupons();
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;
    setEditInput((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleRowsPerPageChange = (event, newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const paginatedTableData = useMemo(() => {
    return filteredCoupons.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredCoupons, page, rowsPerPage]);

  const columns = useMemo(() => [
    {
      Header: "S.No",
      accessor: "s_no",
      align: "center",
      Cell: ({ row }) => (page * rowsPerPage) + row.index + 1,
      disableSortBy: true,
    },
    { Header: "Coupon Code", accessor: "coupon_id", align: "center" },
    { Header: "Discount (%)", accessor: "percentage", align: "center" },
    {
      Header: "Start Date",
      accessor: "start_date",
      align: "center",
      // --- IMPORTANT: Date Formatting Cell ---
      Cell: ({ value }) => value ? new Date(value).toLocaleDateString('en-GB') : 'N/A',
    },
    {
      Header: "End Date",
      accessor: "end_date",
      align: "center",
      // --- IMPORTANT: Date Formatting Cell ---
      Cell: ({ value }) => value ? new Date(value).toLocaleDateString('en-GB') : 'N/A',
    },
    {
      Header: "Options",
      accessor: "options",
      align: "center",
      Cell: ({ row }) => (
        <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
          <IconButton color="success" onClick={() => {
            setOpenEditModal(true);
            const { start_date, end_date, ...rest } = row.original;
            setEditInput({
              ...rest,
              start_date: start_date ? new Date(start_date).toISOString().substring(0, 10) : '',
              end_date: end_date ? new Date(end_date).toISOString().substring(0, 10) : '',
            });
          }}>
            <EditIcon />
          </IconButton>
          <IconButton color="error" onClick={() => {
            setDeleteCouponId(row.original.id);
            setOpenDeleteModal(true);
          }}>
            <DeleteIcon />
          </IconButton>
        </div>
      )
    }
  ], [page, rowsPerPage]);

  const data = useMemo(() => paginatedTableData.map((coupon) => ({
    ...coupon,
  })), [paginatedTableData]);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const response = await api.get("/coupons/");
      if (response.data.status === 1) {
        setCoupons(response.data.coupons);
      } else {
        Swal.fire("Error", response.data.message || "Failed to load coupons", "error");
      }
    } catch (err) {
      console.error("Error fetching coupons:", err);
      Swal.fire("Error", "Server error occurred while fetching coupons.", "error");
    } finally {
      setLoading(false);
    }
  };

  const deleteCoupon = async (e) => {
    e.preventDefault();
    try {
      handleClose();
      const response = await api.delete(`/coupons/${deleteCouponId}`);
      if (response.data.status === 1) {
        Swal.fire({ title: response.data.message, icon: "success", timer: 1500 });
      } else {
        Swal.fire("Error", response.data.message || "Failed to delete coupon", "error");
      }
    } catch (err) {
      console.error("Error deleting coupon:", err);
      Swal.fire("Error", "Unexpected error occurred while deleting coupon.", "error");
    }
  };

  return (
    <DashboardLayout>
      <MDBox display="flex" justifyContent="flex-end" alignItems="center">
        <LogoutButton onLogout={() => navigate("/login")} />
      </MDBox>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info"
                display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6" color="white">Coupon Management</MDTypography>
                
              </MDBox>
              <MDBox   display="flex" justifyContent="space-between" sx={{   mt: 4, mr: 4 }}>
                <TextField
                  label="Search coupons..."
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'black' }} />
                      </InputAdornment>
                    ),
                    style: { color: 'black' }
                  }}
                  InputLabelProps={{
                    style: { color: 'black' }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: 'black' },
                      '&:hover fieldset': { borderColor: 'black' },
                      '&.Mui-focused fieldset': { borderColor: 'black' },
                    },
                    width: '250px',
                    ml: 2
                  }}
                />
                <MDButton color="success" onClick={() => setOpenAddModal(true)}>
                  Add Coupon
                </MDButton>
              </MDBox>
              <MDBox p={3} display="flex" justifyContent="center" alignItems="center" minHeight="50svh">
                {loading ? (
                  <CircularProgress color="primary" />
                ) : (
                  <DataTable
                    table={{ columns, rows: data }}
                    isSorted={true}
                    entriesPerPage={{ defaultValue: rowsPerPage, entries: [5, 10, 15, 20, 25] }}
                    showTotalEntries={true}
                    canSearch={false}
                    noEndBorder
                    pagination={{
                      variant: "contained",
                      color: "dark",
                      page: page,
                      onPageChange: handlePageChange,
                      rowsPerPage: rowsPerPage,
                      onRowsPerPageChange: handleRowsPerPageChange,
                      count: filteredCoupons.length,
                    }}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <CouponAddModel open={openAddModal} handleClose={handleClose} existingCoupons={coupons} /> {/* Pass existing coupons */}

      <CouponEditModal
        open={openEditModal}
        handleClose={handleClose}
        input={editInput}
        setInput={setEditInput}
      />

      <Dialog open={openDeleteModal} onClose={handleClose}>
        <MDBox component="form" onSubmit={deleteCoupon}>
          <DialogTitle>
            <Grid container spacing={2} alignItems="center">
              <Grid item><WarningIcon style={{ color: red[700], fontSize: 40 }} /></Grid>
              <Grid item><MDTypography variant="h6">Confirm Delete</MDTypography></Grid>
            </Grid>
          </DialogTitle>
          <DialogContent>
            <MDBox p={2} style={{ backgroundColor: red[50], borderLeft: `5px solid ${red[700]}` }}>
              <MDTypography>Are you sure you want to delete this coupon? This action cannot be undone.</MDTypography>
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleClose} style={{ color: "gray" }}>Cancel</MDButton>
            <MDButton type="submit" color="primary">Delete</MDButton>
          </DialogActions>
        </MDBox>
      </Dialog>
    </DashboardLayout>
  );
}